import React, {useCallback, useRef, useState} from 'react';
import Button, {getButtonLinkData} from "@/components/common/Buttons/Button";
import Image from "@/components/common/Image";
import Icon from "@/components/common/Icon/Icon";
import Text from "@/components/common/Text";
import {BUTTON_VARIANTS, ICONS} from "@/constants";
import styled from "styled-components";
import {getCssSizeValue, getFontSize, getHtmlFromRedactorField, getIdWithoutSpecialSymbols} from "@/utils/utils";
import {BREAKPOINTS} from "@/styles/themeSettings";
import useScrollContext from "@/hooks/useScrollContext";
import LinkButton from "@/components/common/Buttons/LinkButton";
import TextModal from "@/components/ModalVariants/TextModal";
import useGlobals from "@/hooks/useGlobals";
import {useViewportWidth} from "@/hooks/useViewport";

const ImageWrapper = styled.div`
	position: relative;
	height: 75vh;
	cursor: pointer;
	&.mobile {
		height: ${getCssSizeValue(200, BREAKPOINTS.MOBILE)};
		@media(max-height: 700px) and (max-width: 1024px) {
			display: none;
		}
	}
	@media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
		height: 50vh;
	}
	@media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		height: 75vh;
		position: relative;
	}
	.image {
		height: 100%;
		position: relative;
	}
	.arrow-button-text {
		transition: transform 0.3s ease-in-out, border-radius 0.3s ease-in-out;
		//transform: scaleX(1) translateX(-95%);
		transform: scaleX(1) translateX(90%);
	}
	&.hovered{
		.overlay{
            opacity: 1;
			pointer-events: auto;
		}

        .arrow-button-text {
            transition: transform 0.3s ease-in-out, border-radius 0.3s ease-in-out;
            //transform: scaleX(1) translateX(-95%);
            transform: scaleX(1) translateX(0);
        }
	}
`
const SlidingText = styled.span`
	width: fit-content;
	text-wrap: nowrap;
	height: 100%;
	position: absolute;
	z-index: -1;
	left: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 0 16px;
	border-radius: inherit;
	top: 0;
	background-color: inherit;
	transition: transform 0.3s ease-in-out, border-radius 0.3s ease-in-out;
	transform: scaleX(0);
	transform-origin: left;
	font-size: ${getFontSize(14, BREAKPOINTS.MOBILE)};
	line-height:${getFontSize(14, BREAKPOINTS.MOBILE)};
	font-weight: ${({theme}) => theme.fontWeight.medium};
	@media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
		font-size: ${getFontSize(16, BREAKPOINTS.TABLET)};
		line-height: ${getFontSize(19.2, BREAKPOINTS.TABLET)};
	}
	@media (min-width: ${({theme}) => theme.breakpoints.desktop}px) {
		font-size: ${getFontSize(16, BREAKPOINTS.DESKTOP)};
		line-height: ${getFontSize(19.2, BREAKPOINTS.DESKTOP)};
	}
`



const Overlay = styled.div`
	display: flex;
	flex-direction: column;
	position: absolute;
	inset: 0;
	opacity: 0;
	pointer-events: none;
	color: white;
	align-items: start;
	justify-content: end;
	padding: ${getCssSizeValue(8)};
	row-gap: ${getCssSizeValue(48)};
	
	transition: opacity var(--rgr-duration) var(--ease);
	
	.button{
		margin-left: auto;
		border: unset !important;
	}
	
	.text{
        margin-left: ${getCssSizeValue(20)};
        margin-right: ${getCssSizeValue(104)};
	}
	
    .icon {
        width: 20px;
    }
	
	&:before{
		content: '';
        position: absolute;
        inset: 0;
		z-index: -1;
		opacity: 0.8;
        background-color: ${({ theme }) => theme.colors.richBlack};
	}
`



const PaymentImage = ({ data, className='' }) => {
	const [hover, setHover]=useState(false)
	const [modalOpen, setModalOpen]=useState(false)
	const ContainerRef = useRef()
	const paymentName = data?.paymentName || '';
	const id = getIdWithoutSpecialSymbols(data?.id)
	const image = data?.image;
	const desc = data?.description || '';
	const projectDesc = getHtmlFromRedactorField(desc || paymentName);
	const hasActionButton = Boolean( data?.actionButton);
	const isLinkButton = Boolean(data?.actionButton?.link?.link);
	const button = { id: data?.actionButton?.id || '', ...getButtonLinkData(data?.actionButton || {})}
	const scroll = useScrollContext()
	const paymentModalText = useGlobals('paymentModalText')
	const vWidth = useViewportWidth()
	const handlePointerEnter = useCallback(() => {
		setHover(true)
	},[])

	const handlePointerLeave = useCallback(() => {
		setHover(false)
	},[])

	const handleOpenModal = useCallback(() => {
		setModalOpen(true)
	},[])

	const handleCloseModal = useCallback(() => {
		setModalOpen(false)
	},[])

	const scrollToPayment = useCallback(() => {
		const element = ContainerRef.current
		const scrollValue = scroll.props().scroll
		const paddingTop = vWidth * 0.1047 // 150px na 1920
		const paddingTopOver1536 = Math.min(vWidth * 0.078, 150);
		const finalPaddingTop = vWidth >= 1536 ? paddingTopOver1536 : paddingTop;
		scroll.scrollTo({
			behavior: 'smooth',
			top: scrollValue + element.getBoundingClientRect().top - finalPaddingTop,
		})
	},[scroll, vWidth])


	return (
		<>
			<ImageWrapper
				ref={ContainerRef}
				className={`${className} ${hover ? 'hovered' : ''}`}
				onClick={scrollToPayment}
				onPointerEnter={handlePointerEnter}
				onPointerLeave={handlePointerLeave}
			>
				<Image
					src={image.url}
					alt={image.alt}
					placeholder={image.blurUpThumb}
					focalPoint={image.focalPoint}
				>
					<Overlay className="overlay">
						<Text as="div" className="typo-strawford-29" dangerouslySetInnerHTML={projectDesc} />
						{hasActionButton && (isLinkButton ? (
							(
								<LinkButton href={button.url} variant={BUTTON_VARIANTS.TERTIARY} >
									<Icon name={ICONS.ARROW_DIAGONAL} color='richBlack'/>
									<SlidingText className='arrow-button-text typo-strawford'>{button?.text || ''}</SlidingText>
								</LinkButton>
							)
						) : (
							<>
								<Button variant={BUTTON_VARIANTS.TERTIARY} onClick={handleOpenModal}>
									<Icon name={ICONS.ARROW_DIAGONAL} color='richBlack'/>
									<SlidingText className='arrow-button-text typo-strawford'>{button?.text || ''}</SlidingText>
								</Button>
							</>
						))}
					</Overlay>
				</Image>
			</ImageWrapper>
			<TextModal
				isOpen={modalOpen}
				handleClose={handleCloseModal}
				textContent={paymentModalText}
			/>
		</>
	)
};

export default PaymentImage;
